import { Avatar, Box, Typography } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { useSelector } from "react-redux";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import ButtonOutlinedCentered from "../../components/ButtonOutlined";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import LoadingModal from "../../components/LoadingModal";
import TwoButtonModal from "../../components/TwoButtonModal";
import { colors } from "../../common/colors";
import { convertUserImage, isPostCreator } from "../../util/utils";
import appServices from "../../services/app-services/appServices";

const ViewPostUser = () => {
  const navigate = useNavigate();
  const state = useLocation().state;
  const { user } = useSelector((state: any) => state.userAuth);
  const [postPrivateUsers, setPostPrivateUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<number | null>(null);
  const [showMessage, setShowMessage] = useState<string>("");
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false)
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState<boolean>(false)
  const [iconImage, setIconImage] = useState<any>();

  const post: any = state?.item
  const creator: any = post?.creator

  const getImageSrc = () => {
    if (post?.is_anonymous === 3) {
      setIconImage("");
    } else if (post?.member_group) {
      const group = post?.member_group
      appServices.getGroupIcon(group.id).then(blob => {
        const file = new File([blob], group.group_icon, { type: blob.type });
        setIconImage(URL.createObjectURL(file));
      }).catch(() => {
        console.error("Failed to get group icon image")
        setIconImage('');
      })
    } else if (creator?.image) {
      setIconImage(convertUserImage(creator?.image));
    } else {
      setIconImage("");
    }
  };

  const getName = () => {
    return post?.member_group ? post?.member_group?.name : creator?.nickname || creator?.name || " ";
  };

  const showButton = () => {
    if (isPostCreator(post, user)) {
      return true;
    }
  };

  const handleRemoveUser = async (id: number | null) => {
    if (!id) return false;
    setLoading(true);
    setShowConfirmationModal(null);
    try {
      await appServices.removeUserInPost(id)
      setShowMessage(`${T("view_post_user_deleted")}`);
      setOpenMessageModal(true)
      setPostPrivateUsers((prev: any) => prev.filter((item: any) => item.id !== id));
      return true
    } catch (error: any) {
      setShowMessage(error?.response?.data?.message ?? `${T("add_user_cannot_add")}`)
      setOpenErrorMessageModal(true)
      return false
    } finally {
      setLoading(false);
    };
  };

  useEffect(() => {
    setLoading(true);
    appServices.getPostUsers(post.id).then((res) => {
      const userList = res.post_private_users.filter((privateUser: any) => {
        return privateUser.privater_type !== user.class_name || privateUser.privater_id !== user.id
      })
      setPostPrivateUsers(userList);
    })
    .catch((err) => {
      // pass
    })
    .finally(() => {
      setLoading(false);
    });

    getImageSrc()
  }, []);

  const displayPrivateUser = (privateUser: any) => {
    const privater = privateUser.privater_type === "User" ?
     privateUser.privater?.user : privateUser.privater?.admin_user

    return (
      <Box key={privateUser.id} sx={styles.itemContainer}>
        <Avatar sx={styles.userImage} src={convertUserImage(privater?.image)} />
        <p style={{ ...styles.nameText, fontWeight: "normal" }}>{privater?.nickname}</p>
        {showButton() && <RemoveCircleOutlineIcon onClick={() => setShowConfirmationModal(privateUser.id)} sx={styles.removeIcon} />}
      </Box>
    )
  }

  return (
    <Box sx={styles.container}>
      <LoadingModal visible={loading} />

      <Box sx={styles.headerBox}>
        <GCBackButton onClick={() => navigate(-1)} />
        <Box sx={styles.rowCenter}>
          <Avatar sx={styles.image} src={iconImage} />
          <p style={styles.nameText}>{getName()}</p>
        </Box>
      </Box>

      <Box sx={{ padding: "16px", position: "relative" }}>
        <Typography style={styles.titleSeprator}>{T("post_private_users")}</Typography>
        <Box sx={{ paddingBottom: "100px" }}>
          {postPrivateUsers.map((privateUser: any) => displayPrivateUser(privateUser))}
        </Box>
        {showButton() && (
          <Box sx={{ position: "fixed", bottom: "0px", left: "0px", right: "0px", backgroundColor: "white", padding: "10px" }}>
            <ButtonOutlinedCentered onClick={() => navigate("/addpostuser", { state: { item: post, users: postPrivateUsers } })} title={T("add_users_text")} />
          </Box>
        )}
      </Box>

      <TwoButtonModal
        message={`${T("view_post_user_confirm_delete")}`}
        onContinue={() => handleRemoveUser(showConfirmationModal)}
        open={showConfirmationModal !== null}
        onClose={() => setShowConfirmationModal(null)}
        noText={T("no")}
      />

      <MessageModal
        open={openMessageModal}
        message={showMessage}
        onClose={() => setOpenMessageModal(false)}
      />

      <ErrorMessageModal
        open={openErrorMessageModal}
        messages={[showMessage]}
        onClose={() => setOpenErrorMessageModal(false)}
      />
    </Box>
  );
};

const styles = {
  container: {},
  headerBox: {
    padding: "1px 16px",
    paddingTop: "100px",
    borderBottom: "1px solid #ECEFF1",
  },
  image: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  userImage: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    objectFit: "cover",
    marginLeft: "10px",
  },
  nameText: {
    fontSize: 14,
    fontWeight: 600,
    marginLeft: "10px",
    marginBottom: "0px",
    flex: 1,
  },
  itemContainer: {
    display: "flex",
    alignItems: "center",
    marginRight: "16px",
    marginBottom: "16px",
    borderBottomStyle: "solid" as const,
    borderBottomColor: colors.farm_gray100,
    borderBottomWidth: 1,
    padding: "5px 0px",
  },
  rowCenter: {
    margin: "10px 0px",
    display: "flex",
    alignItems: "center",
  },
  titleSeprator: {
    borderBottomColor: colors.farm_green500,
    borderBottomWidth: 2,
    borderBottomStyle: "solid" as const,
    fontSize: 14,
    fontFamily: "mplus-1c-bold",
    marginBottom: 6,
  },
  removeIcon: {
    fontSize: "34px",
    cursor: "pointer",
    color: colors.farm_red600,
  },
  addButton: {},
};

export default ViewPostUser;
